import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { cfoTranslations } from "../data/cfoFraccionalTranslations";
import { EMPRESAS_LANG_STORAGE_KEY, empresasTranslations } from "../data/empresasTranslations";

const EmpresasLanguageContext = createContext(null);

const normalizeLang = (value) => (value === "en" ? "en" : "es");

const readStoredLang = () => {
  if (typeof window === "undefined") return "es";
  const stored = window.localStorage.getItem(EMPRESAS_LANG_STORAGE_KEY);
  if (stored) return normalizeLang(stored);
  const legacy = window.localStorage.getItem("cfo-lang");
  return normalizeLang(legacy);
};

const getEmpresasBundle = (lang) => {
  const normalized = normalizeLang(lang);
  return empresasTranslations[normalized] ?? empresasTranslations.es;
};

const getCfoBundle = (lang, bundle = getEmpresasBundle(lang)) =>
  bundle?.cfo ?? cfoTranslations[normalizeLang(lang)] ?? cfoTranslations.es;

export function EmpresasLanguageProvider({ children }) {
  const [lang, setLangState] = useState(readStoredLang);

  const setLang = (next) => {
    const value = normalizeLang(next);
    setLangState(value);
    window.localStorage.setItem(EMPRESAS_LANG_STORAGE_KEY, value);
  };

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo(() => {
    const bundle = getEmpresasBundle(lang);
    return {
      lang,
      setLang,
      t: bundle,
      cfo: getCfoBundle(lang, bundle),
    };
  }, [lang]);

  return <EmpresasLanguageContext.Provider value={value}>{children}</EmpresasLanguageContext.Provider>;
}

export function useEmpresasLanguage() {
  const context = useContext(EmpresasLanguageContext);
  if (!context) {
    throw new Error("useEmpresasLanguage must be used within EmpresasLanguageProvider");
  }
  return context;
}

export function useEmpresasLanguageOptional() {
  const context = useContext(EmpresasLanguageContext);
  const location = useLocation();
  const onEmpresas = location.pathname.startsWith("/empresas");

  return useMemo(() => {
    if (!onEmpresas) {
      const bundle = getEmpresasBundle("es");
      return { lang: "es", setLang: () => {}, t: bundle, cfo: getCfoBundle("es", bundle) };
    }
    if (context) {
      return context;
    }
    const lang = readStoredLang();
    const bundle = getEmpresasBundle(lang);
    return {
      lang,
      setLang: () => {},
      t: bundle,
      cfo: getCfoBundle(lang, bundle),
    };
  }, [context, onEmpresas]);
}

export function useCfoBundle() {
  const context = useContext(EmpresasLanguageContext);
  if (!context) {
    throw new Error("useCfoBundle must be used within EmpresasLanguageProvider");
  }
  return context;
}

/** @deprecated Use EmpresasLanguageProvider */
export const CfoLanguageProvider = EmpresasLanguageProvider;

/** @deprecated Use useEmpresasLanguage */
export const useCfoLanguage = useEmpresasLanguage;
